import { lazy, Suspense, useState } from 'react';
import type { JSX } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router';
import type { RouteObject } from 'react-router';
import { ConfigErrorScreen } from '@/auth/ConfigErrorScreen';
import { LOGIN_PATH } from '@/auth/returnPath';
import { AuthConfigError, parseAuthProvider } from '@/config/authProvider';
import type { AuthProvider } from '@/config/authProvider';
import { createAppRouter } from '@/routes/router';
import type { RouterOptions } from '@/routes/router';
import { initTelemetry } from '@/telemetry/consent';
import { exposeTelemetryHandle } from '@/telemetry/inspect';

/**
 * Which login this build carries — a build-time constant, so Vite inlines the
 * literal and Rollup drops the provider that is not selected: a `none` build
 * ships no Amplify and no login screen. `parseAuthProvider` compares the raw
 * value the same way, and fails visibly on anything that is neither.
 */
const IS_COGNITO_BUILD = import.meta.env.VITE_AUTH_PROVIDER === 'cognito';
const CognitoAuthRoot = IS_COGNITO_BUILD
  ? lazy(() => import('@/auth/cognito/CognitoAuthRoot'))
  : null;
const LoginPage = IS_COGNITO_BUILD ? lazy(() => import('@/auth/cognito/LoginPage')) : null;

function resolveProvider(): AuthProvider | AuthConfigError {
  try {
    return parseAuthProvider(import.meta.env.VITE_AUTH_PROVIDER);
  } catch (error) {
    return error instanceof AuthConfigError ? error : new AuthConfigError(String(error));
  }
}

/** Module scope, like the telemetry gate: a property of the build, decided once. */
const authProvider = resolveProvider();

/**
 * Open (or leave shut) the telemetry gate, once, before anything renders.
 *
 * Module scope on purpose. The gate is a property of the build, not of a component
 * lifecycle, and resolving it inside an effect would let the first render's events fall
 * through a gate that was still closed. Default is off: an unset VITE_TELEMETRY_MODE
 * collects nothing.
 */
const telemetry = initTelemetry(import.meta.env.VITE_TELEMETRY_MODE);
if (import.meta.env.DEV) {
  console.info(`[telemetry] ${telemetry.mode} — ${telemetry.because}`);
  exposeTelemetryHandle(true);
}

/**
 * Query client.
 *
 * `retry: 0` because the static adapter cannot fail transiently — a rejection is
 * a real bug and should surface immediately rather than after three silent
 * retries. Revisit when the HTTP adapter lands: network reads DO deserve a
 * retry, and that is a one-line change here.
 *
 * `refetchOnWindowFocus: false` because none of this data changes while the tab
 * is in the background, and a refetch storm on alt-tab is pure noise.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

function RouteFallback(): JSX.Element {
  return (
    <div className="route-loading">
      <div className="skel" style={{ width: '40%', height: 20 }} />
      <div className="skel" style={{ width: '70%' }} />
      <div className="skel" style={{ width: '55%' }} />
    </div>
  );
}

/** The routed application. The session bootstrap now lives inside the route tree, behind the guard. */
function RoutedApp({ publicRoutes }: RouterOptions): JSX.Element {
  // Built once per mount, from the hash the page was opened at. See
  // `createAppRouter` for why this is not a module-level singleton.
  const [router] = useState(() => createAppRouter(publicRoutes ? { publicRoutes } : {}));

  return (
    <Suspense fallback={<RouteFallback />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}

export function App(): JSX.Element {
  if (authProvider instanceof AuthConfigError) {
    return <ConfigErrorScreen message={authProvider.message} />;
  }

  if (authProvider === 'cognito') {
    if (CognitoAuthRoot === null || LoginPage === null) {
      return <ConfigErrorScreen message="This build does not include the cognito provider." />;
    }
    const publicRoutes: readonly RouteObject[] = [{ path: LOGIN_PATH, element: <LoginPage /> }];
    return (
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<RouteFallback />}>
          <CognitoAuthRoot>
            <RoutedApp publicRoutes={publicRoutes} />
          </CognitoAuthRoot>
        </Suspense>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RoutedApp />
    </QueryClientProvider>
  );
}
